import React from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";

import { useLanguage } from "@/contexts/LanguageContext.jsx";
import { logout } from "@/lib/auth/logout.js";

const LOG_OUT_CRITICAL = "/icons/appointments/dashboard/log-out-critical.svg";

export default function SettingsLogoutDialog({ open, onClose }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [pending, setPending] = React.useState(false);

  if (!open || typeof document === "undefined") return null;

  const close = () => {
    if (!pending) onClose?.();
  };

  const leaveFeedback = () => {
    const returnTo = `${location.pathname}${location.search || ""}${location.hash || ""}`;
    onClose?.();
    navigate(`/feedback?intent=signout&from=${encodeURIComponent(returnTo)}`);
  };

  const confirmLogout = async () => {
    if (pending) return;
    setPending(true);

    const failSafe = window.setTimeout(() => {
      window.location.assign("/login?logout=1");
    }, 2500);

    try {
      await logout({ redirectTo: "/login?logout=1" });
    } finally {
      window.clearTimeout(failSafe);
      setPending(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[220] flex items-end justify-center bg-[rgba(15,23,42,0.35)] backdrop-blur-sm md:items-center"
      role="dialog"
      aria-modal="true"
      onClick={close}
    >
      <div
        className="w-full overflow-hidden rounded-t-2xl bg-[rgb(var(--clr-surface-cont-highest))] shadow-[0_18px_45px_rgba(15,23,42,0.18)] md:w-[420px] md:rounded-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="p-5 md:p-6">
          <div className="mx-auto flex w-full max-w-[320px] items-start justify-center gap-3">
            <img src={LOG_OUT_CRITICAL} alt="" className="mt-0.5 h-6 w-6 shrink-0" />
            <div className="min-w-0">
              <div className="text-title-medium text-on-surface">
                {t("settings.home.logoutDialog.title", "Are you sure you want to log out?")}
              </div>
              <div className="mt-1 text-body-medium text-on-surface-var">
                {t(
                  "settings.home.logoutDialog.body",
                  "You'll need to sign in again to continue.",
                )}
              </div>
            </div>
          </div>

          <div className="mt-5 grid gap-3 sm:grid-cols-3">
            <button
              type="button"
              className="rounded-xl px-4 py-3 text-label-large disabled:opacity-60"
              style={{
                background: "var(--State-Layers-Primary-Opacity-08,rgba(29,79,215,0.08))",
                color: "var(--Schemes-Primary,#1D4FD7)",
              }}
              disabled={pending}
              onClick={leaveFeedback}
            >
              {t("feedback.logout.leaveFeedback", "Leave feedback")}
            </button>

            <button
              type="button"
              className="rounded-xl px-4 py-3 text-label-large disabled:opacity-60"
              style={{
                background: "var(--State-Layers-Critical-Opacity-08,rgba(248,83,58,0.08))",
                color: "var(--Schemes-Critical,#F8533A)",
              }}
              disabled={pending}
              onClick={confirmLogout}
            >
              {pending ? t("common.loggingOut", "Logging out...") : t("common.logout", "Log out")}
            </button>

            <button
              type="button"
              className="rounded-xl px-4 py-3 text-label-large disabled:opacity-60"
              style={{
                background: "rgba(24,50,119,0.06)",
                color: "rgb(var(--clr-on-surface-variant))",
              }}
              disabled={pending}
              onClick={close}
            >
              {t("common.cancel", "Cancel")}
            </button>
          </div>
        </div>

        <div className="h-px bg-[rgba(15,23,42,0.06)]" />
      </div>
    </div>,
    document.body,
  );
}
